/**
 * Conectar un monedero de TON y demostrar que es tuyo.
 *
 * ---------------------------------------------------------------------------
 * POR QUE FIRMAR Y NO ESCRIBIR LA DIRECCION
 * ---------------------------------------------------------------------------
 * Porque una direccion escrita a mano es un dato, y cualquiera podria escribir
 * la de otro —o una al azar— y el pago semanal se iria ahi. Una direccion
 * FIRMADA es una prueba de que quien la da tiene la llave (D-034).
 *
 * El servidor ya sabe comprobarla: `app/services/ton.py` lleva el formato de
 * `ton_proof` escrito entero, leido de `docs.ton.org`, con las dos endianness
 * distintas que la especificacion pide en el mismo mensaje. **Aqui no se
 * reinventa nada de eso**: este fichero solo coge lo que devuelve el monedero
 * y lo pone en los nombres que el servidor espera.
 *
 * ---------------------------------------------------------------------------
 * EL SDK SE CARGA TARDE, Y NO ES UN ADORNO
 * ---------------------------------------------------------------------------
 * `@tonconnect/sdk` empaquetado son unos 161 KB crudos. La primera carga va en
 * 675,6 de 700 KB, asi que meterlo de serie la revienta sola. Va por `import()`
 * dentro de `conectar()`: hasta que alguien toca «Conectar monedero» no se
 * descarga ni un byte. Lo mide `npm run peso`, que separa lo que se espera
 * antes del menu de lo que llega despues.
 *
 * `@tonconnect/ui` queda descartado: son 6 MB desempaquetados y arrastra
 * `ua-parser-js` y dos cosas mas para pintar un modal. Se pinta aqui, con
 * botones de texto.
 *
 * ---------------------------------------------------------------------------
 * LO QUE ESTO NECESITA DE LA CSP, Y NO SE PUEDE OLVIDAR
 * ---------------------------------------------------------------------------
 * El monedero habla con el juego por un «puente» HTTP, y la lista de monederos
 * se descarga. Las dos cosas son dominios de terceros, asi que **sin tocar la
 * CSP esto no conecta y no salta ningun error que se vea**: es exactamente lo
 * que paso con `wasm-unsafe-eval` y `blob:`, que tumbaron el personaje 3D sin
 * un solo mensaje a la vista (D-027).
 *
 * Y aqui hay una tension de verdad: **cada monedero tiene SU puente**, y la
 * lista de monederos se descarga en caliente, asi que los origenes no se
 * pueden enumerar de antemano. La lista que trae el SDK dentro lleva puentes
 * de OKX, Bitget, MyTonWallet y media docena mas.
 *
 * La salida no es un comodin en la CSP: es **ofrecer solo los monederos cuyo
 * puente esta permitido**. De ahi `PUENTES_PERMITIDOS`, que tiene que decir lo
 * mismo que el `connect-src` de `infra/cabeceras-seguridad.conf`. Si se
 * separan, el jugador toca «Conectar», la CSP corta la conexion y **no se ve
 * ningun error**: hay un test de paridad que lo impide, igual que con el PRNG.
 *
 * Los valores salen del propio SDK (`dist/tonconnect-sdk.min.js`), no de la
 * memoria de nadie: la lista de produccion es `config.ton.org/wallets-v2.json`
 * —la de `raw.githubusercontent.com` es la de STAGING— y el puente del
 * monedero de Telegram es `walletbot.me/tonconnect-bridge/bridge`.
 *
 * Y ese fichero **se copia a mano al VPS** (`/etc/nginx/snippets/`), nunca por
 * despliegue, con `nginx -t` antes del `reload`: ese nginx lo comparten siete
 * sistemas.
 */

import { modoDev, pedir } from './api';
import {
  cuerpoDeVerificacion,
  LISTA_DE_MONEDEROS,
  PruebaIncompleta,
  PUENTES_PERMITIDOS,
  type MonederoConectado,
} from './pruebaTon';
import { telegram } from './telegram';

// Se reexportan para que quien use el monedero los pida a un solo sitio.
export {
  cuerpoDeVerificacion,
  LISTA_DE_MONEDEROS,
  PruebaIncompleta,
  PUENTES_PERMITIDOS,
} from './pruebaTon';
export type { CuerpoVerificar, MonederoConectado } from './pruebaTon';

/** Lo que el servidor sabe de mi monedero. */
export interface EstadoMonedero {
  direccion: string;
  verificada: boolean;
}

/**
 * El monedero SIMULADO de desarrollo.
 *
 * Fuera de Telegram no hay servidor al que preguntar, y pedirselo de todas
 * formas deja un 500 fijo en la consola. Eso no es gratis: la regla de esta
 * casa es **probar jugando con la consola abierta**, y una consola con un
 * error de siempre ensena a no mirarla. Es el mismo trato que los anuncios,
 * que en local van simulados.
 *
 * La direccion se ve a la legua que es de mentira, a proposito: si apareciera
 * en produccion habria que verlo.
 *
 * **Y el aviso que toca:** este camino simulado hace que la rama de verdad
 * —TON Connect, el puente, la firma— **no se ejecute nunca en local**. Es
 * exactamente lo que paso con los anuncios: el camino de morir SIN anuncios no
 * se habia ejecutado nunca porque en local siempre estaban encendidos. Lo
 * primero al conectar el primer monedero de verdad es abrir la consola.
 */
const MONEDERO_DEV: EstadoMonedero = {
  direccion: '0:dev0000000000000000000000000000000000000000000000000000000dev',
  verificada: true,
};

let simuladoDev: EstadoMonedero = { direccion: '', verificada: false };

/** La url del manifiesto que el monedero descarga para saber quien pide. */
export function urlDelManifiesto(): string {
  return `${window.location.origin}/tonconnect-manifest.json`;
}

/** Abre un enlace de monedero por donde corresponda. */
function abrir(url: string): void {
  if (url.includes('t.me/')) telegram.abrirEnlaceTelegram(url);
  else telegram.abrirEnlace(url);
}

export const monedero = {
  /** Lo que el servidor tiene guardado. */
  mia(): Promise<EstadoMonedero> {
    if (modoDev) return Promise.resolve(simuladoDev);
    return pedir<EstadoMonedero>('/wallet/mia');
  },

  /** Desvincular, que es lo que hace falta para cambiarse de monedero. */
  quitar(): Promise<EstadoMonedero> {
    if (modoDev) {
      simuladoDev = { direccion: '', verificada: false };
      return Promise.resolve(simuladoDev);
    }
    return pedir<EstadoMonedero>('/wallet/quitar', {});
  },

  /**
   * Conecta un monedero y manda la prueba al servidor.
   *
   * El orden importa y es el del spec: **primero se pide el payload al
   * servidor** y luego se conecta pidiendo que lo firme. Al reves, la firma
   * seria de un valor que el servidor no dio y no valdria para nada.
   *
   * `avisar` sirve para contar por donde va: entre pedir el payload y que el
   * jugador vuelva del monedero pasan varios segundos, y una pantalla quieta
   * parece colgada.
   */
  async conectar(avisar: (paso: 'payload' | 'abriendo' | 'esperando' | 'verificando') => void): Promise<EstadoMonedero> {
    if (modoDev) {
      // Simulado, y pasando por los mismos avisos para poder mirar la pantalla
      // en los dos estados. Lo de verdad necesita un manifiesto publico y una
      // persona con monedero: no se puede ejecutar aqui.
      avisar('payload');
      avisar('esperando');
      simuladoDev = MONEDERO_DEV;
      return simuladoDev;
    }

    avisar('payload');
    const { payload } = await pedir<{ payload: string }>('/wallet/nonce');

    // AQUI entra el SDK, y no antes: es lo que lo mantiene fuera de la primera
    // carga. El `import()` tiene la ruta escrita para que vite le haga su
    // propio trozo.
    const { TonConnect, isWalletInfoRemote } = await import('@tonconnect/sdk');

    const conector = new TonConnect({
      manifestUrl: urlDelManifiesto(),
      walletsListSource: LISTA_DE_MONEDEROS,
    });

    // Si ya habia una sesion abierta de antes, se cierra: reusarla saltaria la
    // firma, y sin firma no hay prueba que mandar.
    if (conector.connected) await conector.disconnect();

    // Solo los monederos cuyo puente permite la CSP: ofrecer otro seria
    // ofrecer un boton que corta la conexion sin decir nada.
    const monederos = (await conector.getWallets())
      .filter(isWalletInfoRemote)
      .filter((m) => PUENTES_PERMITIDOS.some((p) => m.bridgeUrl.startsWith(p)));
    // El de Telegram primero, que es el que la mayoria ya tiene dentro.
    const preferido =
      monederos.find((m) => m.universalLink.includes('t.me/')) ?? monederos[0];
    if (!preferido) {
      throw new PruebaIncompleta('no encontre ningun monedero compatible');
    }

    const espera = new Promise<MonederoConectado>((resolver, rechazar) => {
      conector.onStatusChange(
        (w) => {
          if (w) resolver(w as MonederoConectado);
        },
        (err) => rechazar(err instanceof Error ? err : new Error(String(err))),
      );
    });

    avisar('abriendo');
    const url = conector.connect(
      { universalLink: preferido.universalLink, bridgeUrl: preferido.bridgeUrl },
      { request: { tonProof: payload } },
    );
    abrir(typeof url === 'string' ? url : String(url));

    avisar('esperando');
    const conectado = await espera;

    avisar('verificando');
    const cuerpo = cuerpoDeVerificacion(conectado);
    return pedir<EstadoMonedero>('/wallet/verificar', cuerpo);
  },
};
